import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ShieldCheck, KeyRound, Wallet, Info } from "lucide-react";
import { toast } from "sonner";

export default function PaymentGateways() {
  const [cfg, setCfg] = useState({
    razorpay_enabled: false, razorpay_key_id: "", razorpay_key_secret: "", razorpay_webhook_secret: "",
    payu_enabled: false, payu_merchant_key: "", payu_salt: "", payu_merchant_id: "",
    payu_base_url: "https://test.payu.in/_payment",
  });
  const [info, setInfo] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/payments/admin/gateways");
      setInfo(data);
      setCfg(c => ({
        ...c,
        razorpay_enabled: data.razorpay_enabled || false,
        razorpay_key_id: data.razorpay_key_id || "",
        payu_enabled: data.payu_enabled || false,
        payu_merchant_key: data.payu_merchant_key || "",
        payu_merchant_id: data.payu_merchant_id || "",
        payu_base_url: data.payu_base_url || "https://test.payu.in/_payment",
        // secrets stay blank in form (server has them; user types only to change)
        razorpay_key_secret: "", razorpay_webhook_secret: "", payu_salt: "",
      }));
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setCfg(s => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/payments/admin/gateways", cfg);
      toast.success("Gateway settings saved");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="payment-gateways-page">
      <div>
        <div className="section-label">SaaS Console</div>
        <h1 className="font-heading text-3xl font-semibold">Payment gateways</h1>
        <p className="mt-1 text-sm text-ink-muted">Configure Razorpay & PayU credentials. Secrets are stored encrypted and never displayed in full.</p>
      </div>

      <Card className="border-sky-200 bg-sky-50/40 p-4">
        <div className="flex items-start gap-2">
          <Info size={16} className="mt-0.5 text-sky-700" />
          <div className="text-sm text-sky-900">
            <p className="font-medium">Where to get the keys?</p>
            <p className="mt-1"><b>Razorpay:</b> dashboard.razorpay.com → Settings → API Keys (use Test Mode toggle).</p>
            <p><b>PayU India:</b> onboarding.payu.in → Test Mode → Developer → API Keys (Merchant Key + 32-bit Salt).</p>
            <p className="mt-1 italic">Hospitals can pay via either gateway once enabled here.</p>
          </div>
        </div>
      </Card>

      <Tabs defaultValue="razorpay">
        <TabsList>
          <TabsTrigger value="razorpay" data-testid="gateway-tab-razorpay"><Wallet size={14} className="mr-2" />Razorpay</TabsTrigger>
          <TabsTrigger value="payu" data-testid="gateway-tab-payu"><KeyRound size={14} className="mr-2" />PayU India</TabsTrigger>
        </TabsList>

        <TabsContent value="razorpay" className="mt-4">
          <Card className="p-6 space-y-4">
            <div className="flex items-center gap-3">
              <Switch checked={cfg.razorpay_enabled} onCheckedChange={(v) => set("razorpay_enabled", v)} data-testid="razorpay-toggle" />
              <div>
                <Label className="text-base">Enable Razorpay</Label>
                <p className="text-xs text-ink-muted">When ON, hospitals will see Razorpay as a checkout option.</p>
              </div>
              {info.razorpay_key_secret_set && <span className="ml-auto text-xs text-emerald-700 font-medium flex items-center gap-1"><ShieldCheck size={12} /> Secret configured</span>}
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>Key ID</Label>
                <Input value={cfg.razorpay_key_id} onChange={e => set("razorpay_key_id", e.target.value)} placeholder="rzp_test_XXXXXXXXX" data-testid="razorpay-key-id" />
              </div>
              <div>
                <Label>Key Secret {info.razorpay_key_secret_set && <span className="text-xs text-ink-muted ml-1">(current: {info.razorpay_key_secret})</span>}</Label>
                <Input type="password" value={cfg.razorpay_key_secret} onChange={e => set("razorpay_key_secret", e.target.value)} placeholder={info.razorpay_key_secret_set ? "Leave blank to keep current" : "Enter secret"} data-testid="razorpay-key-secret" />
              </div>
              <div className="md:col-span-2">
                <Label>Webhook Secret (optional)</Label>
                <Input type="password" value={cfg.razorpay_webhook_secret} onChange={e => set("razorpay_webhook_secret", e.target.value)} placeholder={info.razorpay_webhook_set ? "Set — leave blank to keep" : "For webhook signature verification"} />
              </div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="payu" className="mt-4">
          <Card className="p-6 space-y-4">
            <div className="flex items-center gap-3">
              <Switch checked={cfg.payu_enabled} onCheckedChange={(v) => set("payu_enabled", v)} data-testid="payu-toggle" />
              <div>
                <Label className="text-base">Enable PayU India</Label>
                <p className="text-xs text-ink-muted">When ON, hospitals will see PayU as a checkout option.</p>
              </div>
              {info.payu_salt_set && <span className="ml-auto text-xs text-emerald-700 font-medium flex items-center gap-1"><ShieldCheck size={12} /> Salt configured</span>}
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>Merchant Key</Label>
                <Input value={cfg.payu_merchant_key} onChange={e => set("payu_merchant_key", e.target.value)} placeholder="gtKFFx" data-testid="payu-merchant-key" />
              </div>
              <div>
                <Label>Salt (32-bit) {info.payu_salt_set && <span className="text-xs text-ink-muted ml-1">(current: {info.payu_salt})</span>}</Label>
                <Input type="password" value={cfg.payu_salt} onChange={e => set("payu_salt", e.target.value)} placeholder={info.payu_salt_set ? "Leave blank to keep current" : "Enter salt"} data-testid="payu-salt" />
              </div>
              <div>
                <Label>Merchant ID (optional)</Label>
                <Input value={cfg.payu_merchant_id} onChange={e => set("payu_merchant_id", e.target.value)} placeholder="For Payment Links API" />
              </div>
              <div>
                <Label>Action URL</Label>
                <Input value={cfg.payu_base_url} onChange={e => set("payu_base_url", e.target.value)} placeholder="https://test.payu.in/_payment (test) or https://secure.payu.in/_payment (live)" />
              </div>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-brand hover:bg-brand-700" data-testid="gateways-save-btn">
          {saving ? "Saving…" : "Save settings"}
        </Button>
      </div>
    </div>
  );
}
